import { AddressForm } from "@/components/address/AddressForm";
import ActionButton from "@/components/buttons/ActionButton";
import HiddenInput from "@/components/form/components/HiddenInput";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { datePickerFormItemProps, datePickerStandardProps, selectStandardProps } from "@/utils/componentUtils";
import { formatAggregatedName, formatPhoneNumber } from "@/utils/formatUtils";
import {
  fillFormBirthDateFromPin,
  phoneNumberNormalizeFunction,
  upperCaseStringNormalizeFunction,
} from "@/utils/formUtils";
import { regexPatterns, validatePhoneNumber, validatePin, validations } from "@/utils/validationUtils";
import { NamePath } from "@rc-component/form/es/interface";
import { App as AntApp, Col, Collapse, DatePicker, Form, Input, Row, Select } from "antd";
import dayjs from "dayjs";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchAgent } from "../../../../../common/api/queries";
import { rowGutter } from "../../../../../common/constants";
import { EducationLevel } from "../../../../../common/enums";
import AcademicDegreeAfterSelect from "../../../../enumerations/components/form/AcademicDegreeAfterSelect";
import AcademicDegreeSelect from "../../../../enumerations/components/form/AcademicDegreeSelect";
import { AgentType, SecondaryAgentType } from "../../../enums";
import {
  CreateUpdateLegalSecondaryAgent,
  CreateUpdateSecondaryAgent,
  CreateUpdateSecondaryAgentRepresentative,
  SecondaryAgentRepresentative,
} from "../../../types";
import { convertPartialAgentToPartialCreateUpdateSecondaryAgent } from "../../../utils";

const colSpan = 4;

type Props = {
  initialData?: SecondaryAgentRepresentative[];
};

export const SecondaryAgentRepresentativesFormPart = ({ initialData }: Props) => {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();

  const [activeKey, setActiveKey] = useState<string | undefined>("0");

  const agentSearch = useSearchAgent();

  const form = Form.useFormInstance<CreateUpdateLegalSecondaryAgent>();
  const representatives = Form.useWatch("representatives", form);

  const handlePinSearchOrChange = (fieldNamePath: NamePath[], fieldIndex: number) => {
    form.validateFields(fieldNamePath).then(async (values) => {
      const pin = values.representatives[fieldIndex]?.personalIdentificationNumber ?? "";

      fillFormBirthDateFromPin(pin, form);
      const searchResult = await agentSearch.mutateAsync({
        keyword: pin,
        agentType: AgentType.NATURAL,
      });

      if (searchResult.data) {
        message.success(t("agent.helpers.agentFound"));

        const formData = convertPartialAgentToPartialCreateUpdateSecondaryAgent(
          searchResult.data,
        ) as Partial<CreateUpdateSecondaryAgent> & { type: SecondaryAgentType.NATURAL };

        form.setFieldsValue({ representatives: { ...(representatives ?? []), [fieldIndex]: formData } });
      }
    });
  };

  return (
    <Form.List
      name="representatives"
      initialValue={[{}]}
      rules={[
        {
          validator: async (_, representatives?: CreateUpdateSecondaryAgentRepresentative[]) => {
            return representatives?.length
              ? Promise.all(
                  representatives?.map(async (representative) => {
                    if (
                      !representative.personalIdentificationNumber ||
                      !representative.firstName ||
                      !representative.lastName ||
                      !representative.birthDate ||
                      !representative.email ||
                      !representative.phone ||
                      !representative.address
                    ) {
                      return Promise.reject(t("agent.helpers.notValidRepresentatives"));
                    }

                    if (
                      representative.firstName.length > 255 ||
                      representative.lastName.length > 255 ||
                      representative.email.length > 254
                    ) {
                      return Promise.reject(t("agent.helpers.notValidRepresentatives"));
                    }

                    if (representative.identityCardNumber) {
                      if (
                        representative.identityCardNumber.length < 8 ||
                        representative.identityCardNumber.length > 10
                      ) {
                        return Promise.reject(t("agent.helpers.notValidRepresentatives"));
                      }

                      if (!regexPatterns.idCardRegex.test(representative.identityCardNumber)) {
                        return Promise.reject(t("agent.helpers.notValidRepresentatives"));
                      }
                    }

                    if (!validatePin(representative.personalIdentificationNumber)) {
                      return Promise.reject(t("agent.helpers.notValidRepresentatives"));
                    }

                    if (!regexPatterns.emailRegex.test(representative.email)) {
                      return Promise.reject(t("agent.helpers.notValidRepresentatives"));
                    }

                    if (!validatePhoneNumber(representative.phone)) {
                      return Promise.reject(t("agent.helpers.notValidRepresentatives"));
                    }

                    return Promise.resolve();
                  }),
                )
              : Promise.reject(t("validation.notNull"));
          },
        },
      ]}
    >
      {(fields, { add, remove }, { errors }) => (
        <>
          <Collapse
            accordion
            style={{ display: representatives?.length ? "block" : "none" }}
            activeKey={activeKey}
            onChange={(key) => setActiveKey(key[0])}
            items={fields.map(({ key, ...field }) => ({
              key: field.name,
              label: (
                <span className={field.name === 0 ? "form-item-required" : ""}>
                  {formatAggregatedName(representatives?.[field.name] ?? {}).trim() ||
                    `${t("agent.helpers.representative")} ${field.name + 1}`}
                </span>
              ),
              children: (
                <React.Fragment key={key}>
                  <HiddenInput name={[field.name, "id"]} />
                  <HiddenInput name={[field.name, "optimisticLockVersion"]} />

                  <Row gutter={rowGutter}>
                    <Col span={colSpan}>
                      <Form.Item
                        name={[field.name, "personalIdentificationNumber"]}
                        label={
                          <LabelWithTooltip
                            tooltip={t("helpers.pinFormat")}
                            label={t("agent.attrs.personalIdentificationNumberLong")}
                          />
                        }
                        rules={[validations.notBlank, validations.pin]}
                      >
                        {!initialData?.[field.name] ? (
                          <Input.Search
                            loading={agentSearch.isPending}
                            onSearch={() =>
                              handlePinSearchOrChange(
                                [["representatives", field.name, "personalIdentificationNumber"]],
                                field.name,
                              )
                            }
                            onChange={() =>
                              handlePinSearchOrChange(
                                [["representatives", field.name, "personalIdentificationNumber"]],
                                field.name,
                              )
                            }
                          />
                        ) : (
                          <Input onChange={(e) => fillFormBirthDateFromPin(e.target.value, form)} />
                        )}
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={rowGutter}>
                    <Col span={colSpan}>
                      <AcademicDegreeSelect
                        formItemProps={{
                          name: [field.name, "academicDegree"],
                          label: t("agent.attrs.academicDegree"),
                        }}
                        selectProps={{ allowClear: true }}
                      />
                    </Col>

                    <Col span={colSpan}>
                      <Form.Item
                        name={[field.name, "firstName"]}
                        label={t("agent.attrs.firstName")}
                        rules={[
                          validations.notBlank,
                          validations.size(1, 255),
                          validations.pattern(regexPatterns.wordRegex),
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>

                    <Col span={colSpan}>
                      <Form.Item
                        name={[field.name, "lastName"]}
                        label={t("agent.attrs.lastName")}
                        rules={[
                          validations.notBlank,
                          validations.size(1, 255),
                          validations.pattern(regexPatterns.wordRegex),
                          validations.pattern(regexPatterns.agentSortNameRegex),
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>

                    <Col span={colSpan}>
                      <AcademicDegreeAfterSelect
                        formItemProps={{
                          name: [field.name, "academicDegreeAfter"],
                          label: t("agent.attrs.academicDegreeAfter"),
                        }}
                        selectProps={{ allowClear: true }}
                      />
                    </Col>

                    <Col span={8}>
                      <Form.Item
                        name={[field.name, "educationLevel"]}
                        rules={[validations.none]}
                        label={t("common.enums.educationLevel._label")}
                      >
                        <Select
                          {...selectStandardProps}
                          allowClear
                          options={Object.values(EducationLevel).map((level) => ({
                            label: t(`common.enums.educationLevel.${level}`),
                            value: level,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={rowGutter}>
                    <Col span={colSpan}>
                      <Form.Item
                        name={[field.name, "birthDate"]}
                        label={t("agent.attrs.birthDate")}
                        rules={[validations.notNull, validations.notPresentAndFuture]}
                        {...datePickerFormItemProps}
                      >
                        <DatePicker {...datePickerStandardProps} showNow={false} maxDate={dayjs().subtract(1, "day")} />
                      </Form.Item>
                    </Col>

                    <Col span={colSpan}>
                      <Form.Item
                        name={[field.name, "identityCardNumber"]}
                        label={t("agent.attrs.identityCardNumber")}
                        rules={[validations.size(8, 10), validations.pattern(regexPatterns.idCardRegex)]}
                        normalize={upperCaseStringNormalizeFunction}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={rowGutter}>
                    <Col span={8}>
                      <Form.Item
                        name={[field.name, "email"]}
                        label={t("agent.attrs.email")}
                        rules={[validations.notBlank, validations.size(1, 254), validations.email]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name={[field.name, "phone"]}
                        label={t("agent.attrs.phone")}
                        rules={[validations.notBlank, validations.mobilePhoneNumber()]}
                        getValueProps={(value) => ({ value: value ? formatPhoneNumber(value) : undefined })}
                        normalize={phoneNumberNormalizeFunction}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>

                  <AddressForm
                    formItemsNamePath={[field.name, "address"]}
                    formFieldsNamePath={["representatives", field.name, "address"]}
                    required
                    label={t("agent.attrs.addressLong")}
                  />
                </React.Fragment>
              ),
              extra:
                field.name > 0 ? (
                  <ActionButton icon="minus" onClick={() => remove(field.name)}>
                    {t("common.remove")}
                  </ActionButton>
                ) : undefined,
            }))}
          />

          <Form.ErrorList className="red-text" errors={errors} />

          <ActionButton
            className="margin-top-small margin-bottom-small"
            icon="plus"
            onClick={() => {
              add({});
              setActiveKey(fields.length.toString());
            }}
          >
            {t("agent.actions.addRepresentative")}
          </ActionButton>
        </>
      )}
    </Form.List>
  );
};
